import React, { useState, useEffect } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { yupResolver } from '@hookform/resolvers/yup';
import { CompanySchema } from "models/CompanySchema";
import TypeAhead from 'components/TypeAhead';
import { endpoints } from 'utils/axios';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
  TextField,
  Button,
  Grid,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Typography,
  Container,
  CircularProgress,
  Breadcrumbs,
  FormHelperText,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Paper, Checkbox,
  Stack, Tooltip, IconButton
} from "@mui/material";
import { useAuth } from "hooks/useAuthContext";
import { useSnackbar } from 'components/snackbar';
import { createCompany, updateCompany, getCompanyStaffList, getCompanyDetails, deleteStaff } from "../../api/company";
import BusinessIcon from '@mui/icons-material/Business';
import { cleanData } from 'data/city.js';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: 6,
  width: 20,
  height: 20,
  boxShadow: 'inset 0 0 0 1.5px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: '#ebf1f5',
    ...theme.applyStyles('dark', {
      backgroundColor: '#30404d',
    }),
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background: 'rgba(206,217,224,.5)',
    ...theme.applyStyles('dark', {
      background: 'rgba(57,75,89,.5)',
    }),
  },
  ...theme.applyStyles('dark', {
    boxShadow: '0 0 0 1px rgb(16 22 26 / 40%)',
    backgroundColor: '#394b59',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
  }),
}));
const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#137cbd',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&::before': {
    display: 'block',
    width: 20,
    height: 20,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#106ba3',
  },
});

function BpCheckbox(props) {
  return (
    <Checkbox
      sx={{ '&:hover': { bgcolor: 'transparent' } }}
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      inputProps={{ 'aria-label': 'Checkbox demo' }}
      {...props}
    />
  );
}

/**
 * Component for managing company information, including the ability to view,
 * create, update, and delete company details. Handles form submission and
 * validation using react-hook-form and yup. Fetches company and staff data
 * for display and provides navigation for adding or editing companies and
 * their staff. This component adjusts behavior based on the user type (admin
 * or recruiter) and read-only status.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.isReadOnly - Determines if the form fields should be
 *   read-only.
 * @returns {JSX.Element} The rendered component.
 */

const CompanyMaster = ({ isReadOnly }) => {
  let { id } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const isRecruiter = user.user_type === 'recruiter';
  const isAdmin = user.user_type === 'admin';
  const [errorCIN, setErrorCIN] = useState(false);
  const [industryList, setIndustryList] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const profile = !!id;
  if (!id) {
    id = user?.id;
  }
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const fromAddCompany = location?.state?.prevPath;
  const [companyDetails, setCompanyDetails] = useState(null);
  const [staffList, setStaffList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userExist, setUserExist] = useState(false);

  const [companyMasterId, setCompanyMasterId] = useState('');

  const methods = useForm({
    defaultValues: {
      full_name: "",
      phone_number: "",
      email: "",
      industry_sector: "",
      type: "",
      website: "",
      incorporation_date: null,
      revenue: "NA",
      description: "",
      location: "",
      total_staff: "NA",
      company_overview: "",
      identification_number: "",
      is_user: false
    },
    mode: "onChange",
    resolver: yupResolver(CompanySchema(isRecruiter)),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    trigger,
    getValues,
    setError,
    control,
    clearErrors,
    formState: { errors, isDirty, isValid, dirtyFields, touched, isSubmitting },
  } = methods;

  useEffect(() => {
    if (fromAddCompany) {
      setLoading(false);
      return;
    }

    /**
     * Fetches company data and sets the form values to the fetched data.
     * If the company response is successful and the data is not null, it sets the company details and
     * user data in the state using the `setValue` method.
     * If the company response is not successful or the data is null, it sets an error message to the snackbar.
     * @throws {Error} if unable to fetch the company data
     */
    const fetchData = async () => {
      try {
        const companyResponse = await getCompanyDetails({ id });
        if (companyResponse?.success && companyResponse?.data) {
          const { staffs, ...restCompanyData } = companyResponse?.data;
          // console.log(companyResponse?.data?.company_master_id);
          setCompanyMasterId(companyResponse?.data?.company_master_id);
          methods.reset(companyResponse?.data);
          setUserExist(restCompanyData?.userExist || false);
          console.log(companyResponse?.data);
          // console.log(staffs);
          setStaffList(staffs || []);
          enqueueSnackbar(companyResponse.message, { variant: 'success' });
        } else {
          enqueueSnackbar(companyResponse.message, { variant: 'error' });
        }

        // const staffResponse = await getCompanyStaffList({ id });

        // if (staffResponse?.success) {
        //   setStaffList(staffResponse.data || []);
        // } else {
        //   enqueueSnackbar(staffResponse.message, { variant: 'error' });
        //   console.error(staffResponse.message);
        // }
      } catch (error) {
        enqueueSnackbar('An unexpected error occurred while fetching company data.', { variant: 'error' });
        // console.error('An unexpected error occurred while fetching company data.', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, enqueueSnackbar, methods]);

  useEffect(() => {
    /**
     * Fetches the list of industries from the server and sets the state with the received data.
     * @throws {Error} if unable to fetch the industry list
     */
    const fetchIndustryList = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.industry}`);
        const data = await response.json();
        setIndustryList(data || []);
      } catch (error) {
        console.error("Failed to fetch industry list", error);
      }
    };

    fetchIndustryList();
  }, []);

  useEffect(() => {
    if (industryList.length > 0) {
      setValue("organization_details.industry_sector", industryList[0].name);
    }
  }, [industryList]);

  /**
   * Handles navigation to the specified path.
   * If the user is not logged in, it navigates to the login page.
   * @param {string} path - The path to navigate to.
   * @returns {undefined}
   */
  const handleNavigation = (path) => {
    if (user) {
      navigate(path);
    } else {
      navigate('/login');
    }
  };

  /**
   * Fetches company data using the provided company ID and updates form values.
   * Sets the loading state before fetching data and resets it after the operation.
   * If company data is successfully retrieved, it updates the form fields for
   * industry sector, company overview, identification number, and website.
   * Triggers validation for these fields if data is available.
   * Logs any errors encountered during the fetch operation.
   * 
   * @param {string} id - The ID of the company to fetch data for.
   * @returns {Promise<void>}
   */

  const handleCompanyData = async (id) => {
    setCompanyLoading(true);
    try {
      setValue('company_id', id || '');
      const companyResponse = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.singleCompanyData}?id=${id}`);
      const companyData = await companyResponse.json();
      console.log("Company Data:", companyData);
      setValue('industry_sector', companyData?.industry_sector || '');
      setValue('company_overview', companyData?.description || '');
      setValue('identification_number', companyData?.identification_number || '');
      setValue('company_website', companyData?.website || '');
      if (companyData?.industry_sector) trigger('industry_sector');
      if (companyData?.identification_number) trigger('identification_number');
      if (companyData?.description) trigger('company_overview');
      if (companyData?.website) trigger('company_website');
    } catch (error) {
      console.error('Error fetching company data:', error);
    }
    setCompanyLoading(false);
  };
  /**
   * Handles the click event on a row in the DataGrid component.
   * Navigates to the staff details page with the staffid as a parameter.
   * @param {string} staffId - The id of the staff whose row was clicked.
   * @returns {undefined}
   */
  const handleRowClick = (staffId) => {
    navigate(`/staff-details/${staffId}`, { state: { companyId: id } });
  };

  const handleStartDateChange = date => {
    setValue('incorporation_date', date.$d);
  }

  const company_types = [
    "Startup",
    "MSME (non-startup)",
    "Large Indian Company",
    "Mid-size MNC (Indian Operations)",
    "Large MNC (Indian Operations)",
    "Government/ PSU",
    "NGO/ Non-profit/ Development sector",
    "Professional firms/ Partnerships",
    "Educational/ Research Institutions",
    "Industry Associations",
    "Miscellaneous",
  ];

  /**
   * Handles the form submission for creating or updating a company.
   * 
   * If `fromAddCompany` is true, it attempts to create a new company with the provided data.
   * If the creation is successful, it displays a success message and navigates to the companies page.
   * Otherwise, it displays an error message.
   * 
   * If `fromAddCompany` is false, it attempts to update an existing company with the provided data.
   * If the update is successful, it displays a success message and navigates based on the profile status.
   * Otherwise, it displays an error message.
   * 
   * Catches and logs any errors that occur during the process, and displays a generic error message.
   * 
   * @param {object} data - The company data to be submitted.
   * @returns {Promise<void>}
   */

  const onSubmit = async (data) => {
    if (!data.incorporation_date) {
      data.incorporation_date = null;
    }
    try {
      let response;
      if (fromAddCompany) {
        response = await createCompany(data);
        if (response?.success && response.data != null) {
          enqueueSnackbar(response.message, { variant: 'success' });
          handleNavigation('/companies');
        } else {
          enqueueSnackbar(response.message, { variant: 'error' });
        }
      } else {
        data.id = id;
        response = await updateCompany(data);
        if (response?.success) {
          enqueueSnackbar(response.message, { variant: 'success' });
          if (profile) {
            handleNavigation('/companies');
          } else {
            handleNavigation('/company-profile');
          }
        } else {
          enqueueSnackbar(response.message, { variant: 'error' });
        }
      }
    } catch (error) {
      enqueueSnackbar('An unexpected error occurred. Please try again later.', { variant: 'error' });
    }
  };
  /**
   * Checks if the given company identification number is valid.
   * It does this by checking if the length of the CIN is 21.
   * @param {string} value - The company identification number as a string.
   * @returns {boolean} True if the CIN is valid, false otherwise.
   */
  const validateCIN = (value) => {
    if (!value) {
      setErrorCIN(false);
      return true;
    }
    if (value && value?.length === 21) {
      setErrorCIN(false);
      return true;
    } else {
      setErrorCIN(true);
      return false;
    }
  };

  const handleDeleteStaff = async (staffId) => {
    try {
      const response = await deleteStaff({ companyId: id, staffId });
      if (response?.success) {
        setStaffList(prev => prev.filter(s => s.staff_id !== staffId));
      } else {
        enqueueSnackbar(data?.message);
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Box style={{ marginTop: '100px' }}>
      <Container >
        {isAdmin && <Breadcrumbs aria-label="breadcrumb" sx={{ marginTop: '1rem', ml: '1rem' }}>
          <Link underline="hover" color="inherit" to="/companies">
            Companies
          </Link>
          <Typography color="text.primary" to={fromAddCompany ? "/edit-profile" : "/company-profile"}>
            {profile ? "Edit Company" : "Add Company"}
          </Typography>
        </Breadcrumbs>}
        <Typography variant="h5" component="div" sx={{ margin: '1rem 0' }}>
          <BusinessIcon /> Company Information
          {!fromAddCompany && (
            <Grid container item xs={12} sx={{ mt: '0', justifyContent: 'flex-end' }} >
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleNavigation(`/add-staff/${id}`)} >
                Add Staff
              </Button>
            </Grid>
          )}
        </Typography>

        {companyMasterId && (
          <Stack direction="row" alignItems="center" spacing={1} mb={3}>
            <Typography variant="h6">Company Id:</Typography>
            <Typography>{companyMasterId || 'N/A'}</Typography>
            <Tooltip title="Copy Company ID">
              <IconButton
                onClick={() => navigator.clipboard.writeText(companyMasterId)}
                size="small"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid item xs={12} sx={{ mt: '3rem' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Controller
                    name="full_name"
                    control={control}
                    render={({ field }) => (
                      !fromAddCompany ? (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Company Name"
                          InputProps={{ readOnly: true }}
                          value={field?.value}
                        />
                      ) : (
                        <TypeAhead
                          {...field}
                          api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.company}`}
                          multiple={false}
                          fullWidth
                          label="Company Name"
                          displayTextKey="name"
                          placeholder="Please enter your company name"
                          freeSolo={true}
                          onInputChange={(e, value) => {
                            setValue("full_name", value, { shouldDirty: true });
                          }}
                          onChange={(e, value) => {
                            if (value) {
                              console.log(value);
                              field.onChange(value?.name);
                              handleCompanyData(value?._id);
                              setValue('company_overview', value?.company_overview || '');
                              setValue('description', value?.description || '');
                              setValue('industry_sector', value?.industry_sector || '');
                              setValue('identification_number', value?.identification_number || '');
                            } else {
                              field.onChange('');
                              setValue('company_overview', '');
                              setValue('description', '');
                              setValue('industry_sector', '');
                              setValue('identification_number', '');
                            }
                          }}
                          error={!!errors.full_name}
                          helperText={errors.full_name?.message}
                        />
                      )
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  {/* company identification number */}
                  <FormControl fullWidth>
                    <Controller
                      name="identification_number"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          error={!!errors.identification_number}
                          helperText={errors.identification_number?.message}
                          label="Company Identification Number (CIN)"
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            validateCIN(e.target.value); // custom CIN validation
                          }}
                          variant="outlined"
                          fullWidth
                          multiline
                        />
                      )}
                    />
                    {errorCIN && (
                      <FormHelperText error>
                        Please enter a 21-character CIN.
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="phone_number"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        size="small"
                        label="Phone Number"
                        {...field}
                        error={!!errors.phone_number}
                        helperText={errors.phone_number?.message}
                        InputProps={{
                          readOnly: userExist,
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Email Address"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        InputProps={{
                          readOnly: userExist,
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small" error={!!errors.industry_sector}>
                    <Controller
                      name="industry_sector"
                      control={control}
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          {...register('industry_sector')}
                          api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.industry}`}
                          size="small"
                          error={!!errors.industry_sector}
                          helperText={errors.industry_sector?.message}
                          label="Industry Sector"
                          displayTextKey="name"
                          variant="outlined"
                          fullWidth
                          placeholder="Industry Sector"
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            trigger('industry_sector');
                          }}
                          value={field.value || ''}
                          disabled={isReadOnly}
                        >
                          {industryList.map((item) => (
                            <MenuItem key={item._id} value={item.name}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </TypeAhead>
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    control={control}
                    name="location"
                    render={({ field }) => (
                      <TypeAhead
                        {...field}
                        {...register('location')}
                        api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.location}`}
                        label="Select location"
                        freeSolo={true}
                        onInputChange={(e, value) => {
                          setValue("location", value, { shouldDirty: true });
                        }}
                        onChange={(e, value) => field.onChange(value)}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.type}>
                        <InputLabel size='small' id="type-label">Company Type</InputLabel>
                        <Select labelId="type-label" label="Company Type"{...field} size="small">
                          <MenuItem value="" disabled>Select Company Type</MenuItem>
                          {company_types.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{errors.type?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="revenue"
                    control={control}
                    defaultValue="NA"
                    render={({ field }) => (
                      <FormControl fullWidth >
                        <InputLabel id="revenue-label" size='small'>Revenue Range (Cr)</InputLabel>
                        <Select
                          labelId="revenue-label"
                          id="revenue"
                          value={field.value ? `${field.value?.min}-${field.value?.max}` : ""}
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "NA") {
                              field.onChange("NA");
                            } else {
                              field.onChange(value);
                            }
                          }}
                          size='small'
                          label="Revenue Range (Cr)"
                        >
                          <MenuItem value="NA" >NA</MenuItem>
                          <MenuItem value="0-5">0-5</MenuItem>
                          <MenuItem value="6-15">6-15</MenuItem>
                          <MenuItem value="16-30">16-30</MenuItem>
                          <MenuItem value="31-75">31-75</MenuItem>
                          <MenuItem value="76-200">76-200</MenuItem>
                          <MenuItem value="201-500">201-500</MenuItem>
                          <MenuItem value="501-1000">501-1000</MenuItem>
                          <MenuItem value="1000-2000">1000+</MenuItem>
                        </Select>
                        {/* <FormHelperText>{errors.revenue?.message}</FormHelperText> */}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="website"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        size='small'
                        {...field}
                        error={!!errors.website}
                        helperText={errors.website && errors.website.message}
                        fullWidth
                        label="Website"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Controller
                    name="total_staff"
                    control={control}
                    defaultValue="NA"
                    render={({ field }) => (
                      <FormControl fullWidth >
                        <InputLabel id="total-staff-label" size="small">Employee Headcount</InputLabel>
                        <Select
                          {...field}
                          labelId="total-staff-label"
                          id="total-staff"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          size="small"
                          label="Employee Headcount"
                        >
                          <MenuItem value="NA">NA</MenuItem>
                          <MenuItem value="0-5">0-5</MenuItem>
                          <MenuItem value="6-15">6-15</MenuItem>
                          <MenuItem value="16-30">16-30</MenuItem>
                          <MenuItem value="31-75">31-75</MenuItem>
                          <MenuItem value="76-200">76-200</MenuItem>
                          <MenuItem value="201-500">201-500</MenuItem>
                          <MenuItem value="501-1000">501-1000</MenuItem>
                          <MenuItem value="1000+">1000+</MenuItem>
                        </Select>
                        {/* <FormHelperText>{errors.total_staff?.message}</FormHelperText> */}
                      </FormControl>
                    )}
                  />

                </Grid>
                <Grid item xs={3}>
                  <Controller
                    name="incorporation_date"
                    fullWidth
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        value={field?.value ? dayjs(field.value) : null}
                        autoOk={true}
                        label='Incorporation Date'
                        closeOnSelect={true}
                        format='DD/MM/YYYY'
                        disableFuture
                        onChange={(date) => handleStartDateChange(date)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: !!errors?.incorporation_date,
                            helperText: errors?.incorporation_date?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="company_overview"
                    control={control}
                    render={({ field }) => {
                      return (
                        <TextField
                          {...field}
                          fullWidth
                          size='small'
                          label="Company Overview (optional)"
                          multiline
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            const words = inputValue.trim().split(/\s+/);
                            if (words?.length <= 30) {
                              setValue('company_overview', inputValue);
                              clearErrors('company_overview');
                            } else {
                              setError('company_overview', { message: "Maximum 30 words allowed." });
                            }
                          }}
                          error={!!errors.company_overview}
                          helperText={errors.company_overview?.message || `${getValues('company_overview') ? getValues('company_overview')?.trim()?.split(/\s+/)?.length || 0 : 0} / 30 words`}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" && !event.shiftKey) {
                              event.preventDefault();
                            }
                          }}
                        />
                      );
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size='small'
                        label="Description (optional)"
                        multiline
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl error={!!errors.is_user} className="neogtiable-chk" size="small">
                    <Controller
                      name="is_user"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <BpCheckbox
                              {...field}
                              id="is_user"
                              name="is_user"
                              checked={!!field.value}
                            />
                          }
                          label="Add company as a user"
                          id="is_user"
                          sx={{ paddingLeft: 0.5 }}
                        />
                      )}
                    />
                    {errors.is_user && (
                      <Typography variant="caption" color="error">
                        {errors.is_user.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid container item xs={12} sx={{ justifyContent: 'center', mb: '1rem' }} >
                  <Button variant="contained" className="next-btn" type="submit" disabled={isSubmitting} size="medium" >
                    {isSubmitting ? "Submitting.." : (!fromAddCompany ? "Update" : "Save")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
        {staffList && staffList.length > 0 && (
          <Grid item xs={12} mb={4}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Business Function</TableCell>
                    <TableCell>Designation</TableCell>
                    <TableCell>Email Address</TableCell>
                    <TableCell>Phone No.</TableCell>
                    <TableCell>TJ Sourced?</TableCell>
                    <TableCell>Actions</TableCell> {/* New column */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staffList && staffList.map((staff, index) => (
                    <TableRow
                      key={staff?.staff_id} // use staff_id since staff is embedded
                      onClick={() => handleRowClick(staff?.staff_id)}
                      hover
                      style={{ cursor: "pointer" }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{staff?.name || "N/A"}</TableCell>
                      <TableCell>{staff?.business_function || "N/A"}</TableCell>
                      <TableCell>{staff?.designation || "N/A"}</TableCell>
                      <TableCell>{staff?.email || "N/A"}</TableCell>
                      <TableCell>{staff?.phone_number || "N/A"}</TableCell>
                      <TableCell>{staff?.is_tj_source ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // stop triggering row click
                            handleDeleteStaff(staff?.staff_id);
                          }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default CompanyMaster;
